import React, { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './ImageCarousel.css';

const defaultImage =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23eee'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='20'%3ENo Image%3C/text%3E%3C/svg%3E";

const ImageCarousel = ({
  images = [],
  autoplay = true,
  loop = true,
  className = "",
  onImageClick,
  title,
  tag,
  viewLabel = "View Details",
  reserveLabel,
  onViewClick,
  onReserveClick,
}) => {
  const plugins = useMemo(
    () =>
      autoplay
        ? [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]
        : [],
    [autoplay]
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      align: "start",
      skipSnaps: false,
      dragFree: false,
      speed: 10,
    },
    plugins
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());

    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const normalizeClassName = (name = "") =>
    name
      .split(" ")
      .filter(
        (cls) => cls && cls !== "owl-carousel" && cls !== "owl-theme"
      )
      .join(" ");

  const getImageSrc = (image) => {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.path || image.url || image.src || "";
  };

  const carouselImages = useMemo(
    () => (images && images.length ? images : [defaultImage]),
    [images]
  );

  const total = carouselImages.length;
  const displayTag = tag ;
  const displayTitle = title ;
  const handleViewClick = () => {
    if (onViewClick) {
      onViewClick(selectedIndex);
      return;
    }

    if (onImageClick) {
      onImageClick(selectedIndex);
    }
  };

  const renderSlides = () => (
    <div className="image-carousel__container">
      {carouselImages.map((image, index) => (
        <div
          key={index}
          className="image-carousel__slide"
          onClick={() => onImageClick && onImageClick(index)}
        >
          <img
            src={getImageSrc(image) || defaultImage}
            alt={`Slide ${index + 1}`}
            className="image-carousel__image"
            loading="lazy"
          />
          <div className="image-carousel__overlay" />
          <div className="image-carousel__label">
            <div className="image-carousel__tag">{displayTag}</div>
            <div className="image-carousel__title">{displayTitle}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`image-carousel ${normalizeClassName(className)}`}>
      <div className="image-carousel__viewport" ref={emblaRef}>
        {renderSlides()}
      </div>
      <button
        type="button"
        className="image-carousel__button image-carousel__button--prev"
        onClick={() => emblaApi?.scrollPrev()}
        aria-label="Previous slide"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        type="button"
        className="image-carousel__button image-carousel__button--next"
        onClick={() => emblaApi?.scrollNext()}
        aria-label="Next slide"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      <div className="image-carousel__bottom-bar">
        <div className="image-carousel__thumb-row">
          {carouselImages.map((image, index) => (
            <button
              key={index}
              type="button"
              className={`image-carousel__thumb ${selectedIndex === index ? "image-carousel__thumb--selected" : ""}`}
              onClick={() => emblaApi?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              <img src={getImageSrc(image) || defaultImage} alt="" loading="lazy" />
            </button>
          ))}
        </div>
        <div className="image-carousel__dots-wrap">
          <div className="image-carousel__dots">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`image-carousel__dot ${selectedIndex === index ? 'image-carousel__dot--selected' : ''}`}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <span className="image-carousel__counter">
            {selectedIndex + 1} / {total}
          </span>
        </div>
        <div className="image-carousel__actions">
          <button
            type="button"
            className="image-carousel__action image-carousel__action--view"
            onClick={handleViewClick}
          >
            {viewLabel}
          </button>
          {reserveLabel && onReserveClick ? (
            <button
              type="button"
              className="image-carousel__action image-carousel__action--reserve"
              onClick={onReserveClick}
            >
              {reserveLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;
